import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, RequiredValidator } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IrisFieldDepartmentModalComponent } from './field-department-modal/field-department-modal.component';
import { IrisDepartmentService } from '../../../services/department.service';
import { IrisQueryParamsBuilder } from '@iris/api-query';
import { IrisFieldBaseComponent } from '../field-base/field-base.component';
import isNil from 'lodash/isNil';
import { FloatLabelType } from '@iris/common/modules/fields/field-label.type';
import { tap } from 'rxjs/operators';
import { StrakitStylingControlDirective } from '@ngx-strakit/cdk';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';

@Component({
  selector: 'iris-field-department',
  templateUrl: './field-department.component.html',
  styleUrls: ['./field-department.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IrisFieldDepartmentComponent),
      multi: true,
    },
  ],
  hostDirectives: [{
    directive: StrakitStylingControlDirective,
    inputs: ['stylingMode'],
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.has-label]': '!!label',
  },
})
export class IrisFieldDepartmentComponent extends IrisFieldBaseComponent implements OnInit, ControlValueAccessor, OnChanges {
  private readonly dialog = inject(MatDialog);
  private readonly departmentService = inject(IrisDepartmentService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  readonly enhancedControl = inject(StrakitStylingControlDirective, { host: true });

  @Input() companyId: number | string | number[];
  @Input() parentId: number | string;
  @Input() downLevel = 0;
  @Input() upLevel = 0;
  @Input() onlyLeaf = false;
  @Input() disabled = false;
  @Input() readonly: boolean;
  @Input() label = '';
  @Input() floatLabel: FloatLabelType = 'auto';

  valueControl: FormControl = new FormControl('');

  @Output() departmentsInit = new EventEmitter<IrisDepartmentI[]>();
  @Output() availableDepartmentsChange = new EventEmitter<IrisDepartmentI[]>();

  departments: IrisDepartmentI[] = [];
  fullSource: IrisDepartmentI[] = [];
  fullSelection: string[] = [];
  selection: string[] = [];
  description = '';
  dSources: unknown[][] = [[]];
  required = (inject(NG_VALIDATORS, { optional: true }) ?? []).findIndex((val) => val.constructor === RequiredValidator) > -1;
  availableDepartments: IrisDepartmentI[] = [];
  private _model: unknown = {};

  propagateChange = (_: unknown): void => undefined;

  set model(model) {
    if (model !== this._model) {
      this._model = model;
      this.propagateChange(model);
      this.refreshControl();
    }
  }

  get model() {
    return this._model;
  }

  ngOnInit(): void {
    this.enhancedControl.stylingMode.set(this.globalSandbox.theme()?.strabagControlsEnabled ? 'automatized' : 'underlined');

    if (!this.departments.length) {
      this.departmentService.getAllDepartments(
        new IrisQueryParamsBuilder().onlyFields(['id', 'name', 'parentId', 'companyId', 'parents']).toStructure(),
      ).pipe(
        tap((res) => {
          this.departmentsInit.emit(res);
          this.departments = res;
          this.availableDepartments = res;
          this.refreshControl();
        }),
      ).subscribe();
    }
  }

  openSelectorModal() {
    if (this.disabled || this.readonly) {
      return;
    }

    const dialogRef: MatDialogRef<IrisFieldDepartmentModalComponent> = this.dialog
      .open(IrisFieldDepartmentModalComponent, {
        data: {
          dSources: this.dSources.slice(),
          departments: this.fullSource.slice(),
          selection: this.selection.slice(),
          options: {
            downLevel: this.downLevel,
            upLevel: this.upLevel,
            onlyLeaf: this.onlyLeaf,
            required: this.required,
            propagate: false,
          },
        },
        maxWidth: '500px',
      });
    dialogRef.componentInstance.result$.pipe(
      tap((res) => {
        if (!res?.length) {
          this.model = null;
          return;
        }
        if (this.onlyLeaf) {
          this.model = res[res.length - 1];
          return;
        } else {
          for (let i = res.length - 1; i >= 0; i--) {
            if (res[i]) {
              this.model = res[i];
              return;
            }
          }
        }
        this.model = null;
      }),
    ).subscribe();
  }

  writeValue(obj: unknown): void {
    if (obj !== undefined) {
      this.model = obj;
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    void 0;
  }

  private refreshControl() {
    this.fullSelection = [];
    this.fullSource = this.availableDepartments;
    this.dSources = [];
    this.description = '';
    if (!isNil(this.companyId)) {
      if (Array.isArray(this.companyId)) {
        this.fullSource = this.fullSource.filter(d => !!(this.companyId as number[]).find(c => c == d.companyId));
      } else {
        this.fullSource = this.fullSource.filter(d => d.companyId == this.companyId);
      }
    }

    if (!isNil(this.parentId)) {
      this.fullSource = this.getChildrenSlice(this.fullSource, this.parentId);
    } else if (this.parentId === null) {
      this.fullSource = [];
    }

    if (!isNil(this.model)) {
      let currentNode = this.fullSource.find(d => d.id == this.model);
      while (currentNode) {
        this.fullSelection.unshift(currentNode.id);
        currentNode = this.fullSource.find(d => d.id == currentNode.parentId);
      }
    } else {
      this.fullSelection.push(null);
    }

    while (this.fullSelection.length <= this.upLevel) {
      this.fullSelection.push(null);
    }

    if (this.downLevel > 1 && this.onlyLeaf) {
      while (this.fullSelection.length < this.upLevel + this.downLevel) {
        this.fullSelection.push(null);
      }
    }

    if (this.upLevel || this.downLevel) {
      if (this.downLevel) {
        this.selection = this.fullSelection.slice(this.upLevel, this.upLevel + this.downLevel);
      } else { this.selection = this.fullSelection.slice(this.upLevel); }
    } else {
      this.selection = this.fullSelection;
    }

    // get top level of hierarchy
    const rootSource = this.getLevelSlice(this.fullSource, this.upLevel, this.parentId);
    this.dSources.push(rootSource);

    // get other levels of hierarchy
    for (let i = 1; i < this.selection.length; i++) {
      this.dSources.push(this.selection[i - 1] ? this.fullSource.filter(d => d.parentId == this.selection[i - 1]) : []);
    }

    // get description
    for (let i = this.selection.length - 1; i >= 0; i--) {
      const currentNode = this.fullSource.find(d => d.id == this.selection[i]);
      if (currentNode) {
        const symbol = this.description ? ' > ' : '';
        this.description = `${currentNode.name}${symbol}${this.description}`;
      }
    }
    this.valueControl.setValue(this.description);
  }

  private getChildrenSlice(source: IrisDepartmentI[], rootId) {
    const res = [];
    let currentLevel = source.filter(t => t.parentId == rootId);
    while (currentLevel.length) {
      res.push(...currentLevel);
      currentLevel = source.filter(t => currentLevel.find(s => t.parentId == s.id));
    }
    return res;
  }

  private getLevelSlice(source: IrisDepartmentI[], level: number, rootId) {
    let currentLevel = source.filter(t => (!t.parentId && !rootId) || (t.parentId == rootId));
    while (level) {
      currentLevel = source.filter(t => currentLevel.find(p => p.id == t.parentId));
      level--;
    }
    return currentLevel;
  }

  clearValue(event: Event): void {
    event.stopPropagation();
    this.valueControl.reset();
    this.model = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.availableDepartments = !this.companyId
      ? [...this.departments]
      : this.departments.filter((department) => department.companyId === this.companyId);
    this.availableDepartmentsChange.emit(this.availableDepartments);
    this.refreshControl();
  }

}
